import React, { useState } from "react";
import { ChevronRight, RotateCcw, ClipboardList } from "lucide-react";

// Tailwind-styled components
const Card = ({ children }) => <div className="bg-white p-6 shadow-lg rounded-lg">{children}</div>;
const CardHeader = ({ children }) => <div className="mb-4">{children}</div>;
const CardTitle = ({ children }) => <h2 className="text-2xl font-bold text-brand">{children}</h2>;
const CardContent = ({ children }) => <div>{children}</div>;
const Button = ({ children, onClick }) => (
  <button
    className="w-full bg-brand text-white py-2 px-4 rounded hover:bg-brand-dark transition-all"
    onClick={onClick}
  >
    {children}
  </button>
);
const Progress = ({ value }) => (
  <div className="w-full bg-gray-200 rounded-full h-2">
    <div className="bg-brand h-2 rounded-full" style={{ width: `${value}%` }}></div>
  </div>
);
const Alert = ({ children }) => <div className="bg-green-100 p-4 rounded">{children}</div>;
const AlertTitle = ({ children }) => <h3 className="font-bold text-green-700">{children}</h3>;
const AlertDescription = ({ children }) => <p className="text-green-600">{children}</p>;

export default function BodyShapeDietQuiz() {
  // Quiz questions
  const questions = [
  // Physical Appearance - Head & Face
  {
    category: "Physical Appearance",
    question: "What is your head and face shape?",
    options: [
      { text: "Square or rectangular shaped head with thickened facial tissues", scores: { Thyroid: 3, Adrenal: 2 } },
      { text: "Round or baby-like face with smooth features and prominent cheeks", scores: { Pituitary: 3} },
      { text: "Small head compared to body size", scores: { Pituitary: 2, Adrenal: 2 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  {
    category: "Physical Appearance",
    question: "How would you describe your facial features?",
    options: [
      { text: "Puffy/swollen face, especially around eyes, with 	ing corners", scores: { Thyroid: 3, Pituitary: 2 } },
      { text: "Short/upturned nose (often pug-like) with full lips", scores: { Pituitary: 2, Adrenal: 2 } },
      { text: "Lost contour of facial bones with thickened tissues", scores: { Thyroid: 2 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  // Body Frame
  {
    category: "Physical Appearance",
    question: "How would you describe your body frame and height?",
    options: [
      { text: "Medium to large-framed", scores: { Thyroid: 3, Adrenal: 2 } },
      { text: "Small bone frame with small, sloping shoulders", scores: { Pituitary: 3, Adrenal: 1 } },
      { text: "Medium height with rectangular/square body shape", scores: { "Thyroid-Adrenal": 2 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  // Weight Distribution
  {
    category: "Weight Distribution",
    question: "Where do you tend to accumulate fat?",
    options: [
      { text: "Front of abdomen and thighs with love handles", scores: { Thyroid: 3 } },
      { text: "Lower abdomen, hips, buttocks, and thighs (pear shape)", scores: { Pituitary: 3 } },
      { text: "Layer of fat over entire body", scores: { Pituitary: 3, Adrenal: 2 } },
      { text: "Lower front abdomen and along hips", scores: { "Thyroid-Adrenal": 3 } }
    ]
  },
  {
    category: "Weight Distribution",
    question: "How would you describe your buttocks?",
    options: [
      { text: "Flattened buttocks with possible cellulite", scores: { Thyroid: 3 } },
      { text: "Prominent buttocks, extremely large compared to frame", scores: { Pituitary: 3, Adrenal: 3 } },
      { text: "Less fat in buttocks compared to pure thyroid type", scores: { "Thyroid-Adrenal": 2 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  // Hand/Finger Characteristics
  {
    category: "Hand/Finger Characteristics",
    question: "Compare your index finger to your ring finger on your dominant hand:",
    options: [
      { text: "Index finger shorter than ring finger", scores: { Thyroid: 3 } },
      { text: "Index finger longer than ring finger", scores: { Adrenal: 3 } },
      { text: "Different pattern on each hand (one hand opposite of other)", scores: { "Thyroid-Adrenal": 3 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  {
    category: "Hand/Finger Characteristics",
    question: "How would you describe your hands and fingers?",
    options: [
      { text: "Stubby/medium length hands with puffy fingers", scores: { Thyroid: 3 } },
      { text: "Small hands and feet compared to body size", scores: { Pituitary: 2, Adrenal: 2 } },
      { text: "Vertical ridges on fingernails and/or white spots", scores: { Thyroid: 2 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  // Symptoms - Temperature & Energy
  {
    category: "Symptoms",
    question: "How do you experience body temperature?",
    options: [
      { text: "Cold hands/feet and low body temperature", scores: { Thyroid: 3, Adrenal: 2 } },
      { text: "Cold extremities with poor circulation", scores: { Thyroid: 1, Adrenal: 3 } },
	  { text: "Higher body temperature or more sensitive to heat", scores: { Pituitary: 2 } },
      { text: "Mixed temperature patterns", scores: { "Thyroid-Adrenal": 2 } }
    ]
  },
  {
    category: "Symptoms",
    question: "What is your typical energy pattern?",
    options: [
      { text: "Morning fatigue but energetic at night", scores: { Thyroid: 3 } },
      { text: "Often tired and requires much sleep", scores: { Pituitary: 3, Adrenal: 1 } },
      { text: "May fall asleep at work during day", scores: { "Thyroid-Adrenal": 2 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  // Cognitive Symptoms
  {
    category: "Symptoms",
    question: "How would you describe your mental function?",
    options: [
      { text: "Brain fog and poor concentration", scores: { Thyroid: 3, Pituitary: 2 } },
      { text: "Mental sluggishness with poor concentration", scores: { Pituitary: 3 } },
      { text: "Problems with concentration (mixed symptoms)", scores: { "Thyroid-Adrenal": 2 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  // Speech and Voice
  {
    category: "Symptoms",
    question: "How would you describe your speech and voice?",
    options: [
      { text: "Slow/slurred speech with hoarseness/coarse voice", scores: { Thyroid: 3 } },
      { text: "Normal speech but tends to tire when speaking", scores: { Pituitary: 1, Adrenal: 1 } },
      { text: "Mixed voice characteristics", scores: { "Thyroid-Adrenal": 1 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  // Hair and Skin
  {
    category: "Symptoms",
    question: "How would you describe your hair and skin?",
    options: [
      { text: "Dry/coarse hair and skin, possible outer eyebrow loss", scores: { Thyroid: 3 } },
      { text: "Fine hair texture, delicate skin, sparse body hair", scores: { Pituitary: 3, Adrenal: 1, Thyroid: 1 } },
      { text: "Medium to medium-coarse hair texture", scores: { "Thyroid-Adrenal": 2 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  // Digestion
  {
    category: "Symptoms",
    question: "How would you describe your digestion?",
    options: [
      { text: "Poor digestion/bloating after eating, constipation", scores: { Thyroid: 3 } },
      { text: "Food sensitivities common, difficulty with fats/greasy foods", scores: { Pituitary: 2, Adrenal: 2 } },
      { text: "Mixed digestive issues", scores: { "Thyroid-Adrenal": 2 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  // Chemical and Environmental Sensitivity
  {
    category: "Symptoms",
    question: "How sensitive are you to chemicals, medications, and environment?",
    options: [
      { text: "Some sensitivity to chemicals", scores: { Thyroid: 1 } },
      { text: "Highly sensitive to chemicals/medications/fumes/odors", scores: { Pituitary: 3, Adrenal: 3 } },
      { text: "Moderate sensitivity", scores: { "Thyroid-Adrenal": 1 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  // Medical History
  {
    category: "Medical History",
    question: "Do you have any of these health conditions?",
    options: [
      { text: "High cholesterol, heart disease, high blood pressure", scores: { Thyroid: 3 } },
      { text: "History of respiratory infections, chronic infections", scores: { Adrenal: 4 } },
      { text: "History of ovarian cysts", scores: { Thyroid: 2 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  {
    category: "Medical History",
    question: "Is there a relevant family history of:",
    options: [
      { text: "Thyroid disorders", scores: { Thyroid: 3 } },
      { text: "TB or chronic infections", scores: { Adrenal: 3 } },
      { text: "Multiple endocrine issues", scores: { "Thyroid-Adrenal": 2 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  // Diet/Metabolism
  {
    category: "Diet/Metabolism",
    question: "How would you describe your metabolism and carbohydrate tolerance?",
    options: [
      { text: "Slow metabolism, poor carbohydrate tolerance", scores: { Thyroid: 3 } },
      { text: "Extremely slow metabolism, difficulty processing carbs", scores: { Pituitary: 3, Adrenal: 2 } },
      { text: "Mixed metabolic rate", scores: { "Thyroid-Adrenal": 2 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  {
    category: "Diet/Metabolism",
    question: "How do you handle different types of foods?",
    options: [
      { text: "Strong need for protein, poor digestion of grains", scores: { Thyroid: 3 } },
      { text: "Does poorly with heavy fats, tolerates fruit well", scores: { Pituitary: 2, Adrenal: 2 } },
      { text: "Can handle fatty foods well, animal proteins well tolerated", scores: { "Thyroid-Adrenal": 2 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  // Stress Response
  {
    category: "Response to Stress",
    question: "How do you typically respond to stress?",
    options: [
      { text: "Variable stress tolerance", scores: { Thyroid: 2 } },
      { text: "Poor coping capacity, easily drained, may collapse after stress", scores: { Adrenal: 3 } },
      { text: "Strong negative reaction to emotional stress", scores: { Adrenal: 2 } },
      { text: "Mixed response patterns", scores: { "Thyroid-Adrenal": 2 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  // Growth and Development
  {
    category: "Symptoms",
    question: "How would you describe your growth patterns?",
    options: [
      { text: "Slow growing hair and nails", scores: { Thyroid: 2 } },
      { text: "Poor hand-eye coordination", scores: { Thyroid: 2 } },
      { text: "Normal growth patterns", scores: { Pituitary: 1, Adrenal: 1 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  // Additional Physical Characteristics
  {
    category: "Physical Appearance",
    question: "How would you describe your neck region?",
    options: [
      { text: "Neck stiffness and tension", scores: { Thyroid: 3 } },
      { text: "Normal neck mobility", scores: { Pituitary: 1, Adrenal: 1 } },
      { text: "Variable neck comfort", scores: { "Thyroid-Adrenal": 1 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  {
    category: "Physical Appearance",
    question: "Do you have any issues with fluid retention?",
    options: [
      { text: "General puffiness/swelling throughout body", scores: { Thyroid: 3 } },
      { text: "Prone to fluid retention", scores: { Pituitary: 2, Adrenal: 2 } },
      { text: "Occasional swelling", scores: { "Thyroid-Adrenal": 1 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  // Additional Medical Symptoms
  {
    category: "Symptoms",
    question: "Do you experience any of these cardiovascular symptoms?",
    options: [
      { text: "Heart palpitations", scores: { Thyroid: 3 } },
      { text: "Dizziness or fainting after standing quickly", scores: { Adrenal: 4, Thyroid: 2 } },
      { text: "No significant cardiovascular symptoms", scores: { "Thyroid-Adrenal": 1 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  {
    category: "Symptoms",
    question: "Do you have any of these specific health issues?",
    options: [
      { text: "Fungal infections (toenails, skin)", scores: { Thyroid: 2 } },
      { text: "Allergic tendencies", scores: { Pituitary: 2, Adrenal: 2 } },
      { text: "No significant infections or allergies", scores: { "Thyroid-Adrenal": 1 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  // Additional Reproductive Health
  {
    category: "Symptoms",
    question: "Do you experience any reproductive health issues?",
    options: [
      { text: "PMS/menstrual difficulties", scores: { Thyroid: 2 } },
      { text: "History of reproductive health issues", scores: { Thyroid: 2, Adrenal: 1 } },
      { text: "No significant reproductive issues", scores: { "Thyroid-Adrenal": 1 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  // Additional Diet/Metabolism
  {
    category: "Diet/Metabolism",
    question: "How do you respond to different oil types?",
    options: [
      { text: "Does well with heavy oils like poppy seed oil", scores: { "Thyroid-Adrenal": 2 } },
      { text: "Needs essential fatty acids (omega-3s)", scores: { Pituitary: 2, Adrenal: 2 } },
      { text: "Tolerates most oils well", scores: { Thyroid: 1 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  // Location-Based
  {
    category: "Medical History",
    question: "Do you live in or come from:",
    options: [
      { text: "Great Lakes region or similar iodine-deficient areas", scores: { Thyroid: 2 } },
      { text: "Other regions", scores: { "Thyroid-Adrenal": 1, Adrenal: 1 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  // Specific Pain Patterns
  {
    category: "Symptoms",
    question: "Do you experience any of these pain patterns?",
    options: [
      { text: "Chronic headaches (especially after age 40)", scores: { Thyroid: 2 } },
      { text: "Pain behind eyes or in forehead", scores: { Pituitary: 2, Adrenal: 2 } },
      { text: "Joint stiffness", scores: { Thyroid: 2 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
   // Stress Response
  {
    category: "Response to Stress",
    question: "How do you typically handle stress?",
    options: [
      { text: "Feel energized initially, then crash under stress", scores: { Adrenal: 4 } },
      { text: "Struggle to cope, feel drained immediately", scores: { Pituitary: 2 } },
      { text: "Variable stress tolerance", scores: { Thyroid: 2 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  // Dietary Cravings
  {
    category: "Diet/Cravings",
    question: "What foods do you crave most?",
    options: [
      { text: "Salty snacks", scores: { Adrenal: 3 } },
      { text: "Sugary foods", scores: { Pituitary: 3 } },
      { text: "Fatty or fried foods", scores: { Thyroid: 3 } },
      { text: "No specific cravings", scores: { "Thyroid-Adrenal": 2 } }
    ]
  },
  // Sleep Patterns
  {
    category: "Symptoms",
    question: "How would you describe your sleep?",
    options: [
      { text: "Sleep soundly but feel tired on waking", scores: { Thyroid: 3 } },
      { text: "Wake up frequently or struggle to fall asleep", scores: { Adrenal: 3 } },
      { text: "Need extra sleep and still feel tired", scores: { Pituitary: 3 } },
      { text: "Sleep well with no issues", scores: { "Thyroid-Adrenal": 2 } }
    ]
  },
  // Energy Levels Throughout the Day
  {
    category: "Symptoms",
    question: "How would you describe your energy throughout the day?",
    options: [
      { text: "High energy in the morning but crash in the afternoon", scores: { Thyroid: 2 } },
      { text: "Low energy all day but slightly better in the evening", scores: { Pituitary: 3 } },
      { text: "Variable energy levels with no consistent pattern", scores: { Adrenal: 3, Thyroid: 1 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  // Exercise Tolerance
  {
    category: "Lifestyle",
    question: "How do you tolerate exercise?",
    options: [
      { text: "Feel energized after light exercise, drained after intense exercise", scores: { Adrenal: 3 } },
      { text: "Struggle to start and maintain exercise routines", scores: { Pituitary: 3 } },
      { text: "Can perform moderate exercise without issues", scores: { Thyroid: 2 } },
      { text: "No consistent pattern of response to exercise", scores: { "Thyroid-Adrenal": 2 } }
    ]
  },
  // Emotional Reactivity
  {
    category: "Symptoms",
    question: "How do you respond emotionally to challenges?",
    options: [
      { text: "Feel highly emotional and cry easily", scores: { Pituitary: 3 } },
      { text: "Feel angered or stressed easily", scores: { Adrenal: 3 } },
      { text: "Remain calm but tend to internalize stress", scores: { Thyroid: 2 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  // Immune System
  {
    category: "Symptoms",
    question: "How often do you experience illnesses like colds or infections?",
    options: [
      { text: "Frequent illnesses (e.g., colds, flu, infections)", scores: { Adrenal: 3 } },
      { text: "Occasional illnesses but recover slowly", scores: { Thyroid: 2 } },
      { text: "Rarely sick and recover quickly", scores: { Pituitary: 2 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  // Digestive Health
  {
    category: "Symptoms",
    question: "How would you describe your digestive health?",
    options: [
      { text: "Frequent bloating and constipation", scores: { Thyroid: 3 } },
      { text: "Sensitive stomach, difficulty with fats or greasy foods", scores: { Pituitary: 3, Adrenal: 2, Thyroid: 1 } },
      { text: "Mixed symptoms with no clear pattern", scores: { "Thyroid-Adrenal": 2 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  // Mental Focus
  {
    category: "Symptoms",
    question: "How would you describe your mental focus?",
    options: [
      { text: "Easily distracted, poor concentration", scores: { Pituitary: 3 } },
      { text: "Sharp in the morning but lose focus as the day goes on", scores: { Thyroid: 3 } },
      { text: "Mixed ability to concentrate depending on stress levels", scores: { Adrenal: 2 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  }, 
  // Blood Chemistry
  {
    category: "Medical History",
    question: "Do you have any of these blood chemistry patterns?",
    options: [
      { text: "Low blood sodium levels", scores: { Adrenal: 3 } },
      { text: "High cholesterol", scores: { Thyroid: 3 } },
      { text: "Normal blood chemistry", scores: { "Thyroid-Adrenal": 1 } },
	  { text: "None of these describe me", scores: { Pituitary: 0 } }
    ]
  },
  {
	category: "Physical Appearance",
	question: "Do you notice any facial asymmetry?",
	options: [
		{ text: "Yes, subtle but noticeable", scores: { Pituitary: 3 } },
		{ text: "No noticeable asymmetry", scores: { "Thyroid-Adrenal": 1 } },
		{ text: "None of these describe me", scores: { Pituitary: 0 } }
	]
  },
];
  // Recommendations for each body type
const recommendations = {
	  Thyroid: {
		diet: [
		  "Emphasis on animal protein from organic sources",
		  "Large amounts of salad greens",
		  "Cooked cruciferous vegetables only (no raw)",
		  "Seafood for natural iodine/salts",
		  "Alternative grains like teff, quinoa, amaranth",
		  "Avoid refined sugar, sweets, chocolate",
		  "Limited nuts and seeds"
		],
		supplements: [
		  {
			text: "Body shape Thyroid type formula",
			link: "https://purelywildnatural.com/collections/body-shape-diet-endocrine-support-system"
		  },
		  {
			text: "Wild oregano and sumac",
			link: "https://purelywildnatural.com/collections/the-wild-mountain-oregano-collection"
		  },
		  "Royal jelly",
		  "Remote-source kelp with wild oregano and tyrosine",
		  "B vitamins from rice bran/polish concentrates"
		],
		lifestyle: [
		  "Regular exercise focusing on metabolism activation",
		  "Maintain consistent sleep schedule",
		  "Stress management techniques",
		  "Avoid fluoride exposure",
		  "Focus on organic/wild-sourced foods"
		]
	  },
	  Adrenal: {
		diet: [
		  "Liberal use of salt",
		  "Fatty and protein-rich foods",
		  "Whole organic eggs and dairy products",
		  "Limited vegetables",
		  "Can handle some complex carbohydrates",
		  "Foods rich in cholesterol",
		  "Fruit superior to vegetables"
		],
		supplements: [
		  {
			text: "Body shape Adrenal type formula",
			link: "https://purelywildnatural.com/collections/body-shape-diet-endocrine-support-system"
		  },
		  "Undiluted 3x royal jelly with wild sage and rosemary",
		  "Natural vitamin C",
		  "Wild raw triple greens",
		  "Pantothenic acid sources like royal jelly, bee pollen"
		],
		lifestyle: [
		  "Stress management essential",
		  "Regular rest periods",
		  "Gentle exercise only",
		  "Avoid overstimulation",
		  "Poor coping capacity with stress"
		]
	  },
	  Pituitary: {
		diet: [
		  "Focus on lean proteins",
		  "Essential fatty acid rich foods",
		  "Low sugar fruits",
		  "Liberal salad consumption",
		  "Wild greens daily",
		  "Avoid heavy greasy foods",
		  "Focus on fish if unpolluted"
		],
		supplements: [
		  "Essential fatty acids (sacha inchi oil)",
		  "Wild greens extracts",
		  "Multiple berry concentrates",
		  "Zinc-rich foods and supplements"
		],
		lifestyle: [
		  "Gentle exercise routines",
		  "Stress reduction practices",
		  "Adequate rest and sleep",
		  "Avoid chemical exposures"
		]
	  },
	  "Thyroid-Adrenal": {
		diet: [
		  "Balance of proteins and healthy fats",
		  "Moderate whole grains (avoid wheat)",
		  "Good balance of saturated fats and omega-3s",
		  "Red meat and wild game in quantity",
		  "Fish and seafood in moderation",
		  "Emphasize fruit over vegetables",
		  "High-quality omega-3 oils for salads"
		],
		supplements: [
		  "Royal jelly",
		  {
			text: "Wild oregano and sumac",
			link: "https://purelywildnatural.com/collections/the-wild-mountain-oregano-collection"
		  },
		  "Kelp with wild oregano and tyrosine",
		  "B-vitamins from natural sources",
		  "Wild raw greens extracts"
		],
		lifestyle: [
		  "Balanced approach to exercise",
		  "Combined stress management techniques",
		  "Focus on organic/wild-sourced foods",
		  "Avoid processed foods and GMOs",
		  "Needs balanced approach considering both glandular systems"
		]
	  }
	};
  // State variables
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [scores, setScores] = useState({
    Thyroid: 0,
    Pituitary: 0,
    Adrenal: 0,
    "Thyroid-Adrenal": 0,
  });

  // Handle answer selection
  const handleAnswer = (optionIndex) => {
    const newAnswers = { ...answers, [currentQuestion]: optionIndex };
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      calculateFinalScores(newAnswers);
    }
  };

  // Calculate scores based on answers
  const calculateFinalScores = (finalAnswers) => {
    const newScores = { ...scores };

    Object.entries(finalAnswers).forEach(([questionIndex, optionIndex]) => {
      const selectedOption = questions[questionIndex].options[optionIndex];
      Object.entries(selectedOption.scores).forEach(([type, score]) => {
        newScores[type] += score;
      });
    });

    setScores(newScores);
    setShowResults(true);
  };

  // Get dominant body type
  const getDominantType = () => {
    return Object.entries(scores).reduce((a, b) => (a[1] > b[1] ? a : b))[0];
  };

  // Reset quiz
  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setScores({
      Thyroid: 0,
      Pituitary: 0,
      Adrenal: 0,
      "Thyroid-Adrenal": 0,
    });
  };
    // Add this new function here
  const renderSupplementItem = (item) => {
    if (typeof item === 'string') {
      return <li key={item}>{item}</li>;
    }
    return (
      <li key={item.text}>
        <a 
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline"
        >
          {item.text}
        </a>
      </li>
    );
  };
  // Main UI
		return (
	  <div className="w-full max-w-screen-sm mx-auto px-4 py-6 sm:px-6 lg:px-8 min-h-screen bg-gray-50">
		<Card className="w-full shadow-lg rounded-lg">
		  {/* Quiz Header */}
		  <CardHeader>
			<CardTitle className="text-center text-xl sm:text-2xl font-bold text-brand">
			  Body Shape Diet Type Quiz
			</CardTitle>
			{!showResults && (
			  <div className="mt-4">
				<Progress
				  value={((currentQuestion + 1) / questions.length) * 100}
				  className="h-2"
				/>
				<p className="text-sm text-gray-500 mt-2 text-center">
				  Question {currentQuestion + 1} of {questions.length}
				</p>
			  </div>
			)}
		  </CardHeader>

		  {/* Quiz Body */}
		  <CardContent>
			{!showResults ? (
			  <div className="space-y-6">
				{/* Question */}
				<h3 className="text-lg sm:text-xl font-medium text-gray-800 text-center">
				  {questions[currentQuestion].question}
				</h3>

				{/* Answer Options */}
				<div className="space-y-3">
				  {questions[currentQuestion].options.map((option, index) => (
					<Button
					  key={index}
					  className="w-full p-2 sm:p-3 text-sm sm:text-base rounded-lg flex items-center justify-start bg-gray-100 hover:bg-gray-200 transition-all duration-300"
					  onClick={() => handleAnswer(index)}
					>
					  <ChevronRight className="mr-2 h-5 w-5 text-brand" />
					  {option.text}
					</Button>
				  ))}
				</div>
			  </div>
			) : (
			  <div className="space-y-6">
				{/* Results Header */}
				<Alert>
				  <ClipboardList className="h-6 w-6 text-green-600 inline-block mb-1" />
				  <AlertTitle className="text-lg font-bold">Your Results</AlertTitle>
				  <AlertDescription>
					Based on your answers, your primary body type is:{" "}
					<strong className="text-brand">{getDominantType()}</strong>
				  </AlertDescription>
				</Alert>

				{/* Recommendations */}
				<div className="space-y-6">
				  {/* Key Characteristics */}
				  <div>
					<h3 className="font-semibold text-lg mb-2">Key Characteristics:</h3>
					<ul className="list-disc pl-5 space-y-1">
					  {recommendations[getDominantType()].characteristics?.map(
						(char, index) => <li key={index}>{char}</li>
					  )}
					</ul>
				  </div>

				  {/* Dietary Recommendations */}
				  <div>
					<h3 className="font-semibold text-lg mb-2">Dietary Recommendations:</h3>
					<ul className="list-disc pl-5 space-y-1">
					  {recommendations[getDominantType()].diet.map((item, index) => (
						<li key={index}>{item}</li>
					  ))}
					</ul>
				  </div>

				  {/* Supplements */}
				  <div>
					<h3 className="font-semibold text-lg mb-2">Recommended Supplements:</h3>
					<ul className="list-disc pl-5 space-y-1">
					  {recommendations[getDominantType()].supplements.map((item) =>
						renderSupplementItem(item)
					  )}
					</ul>
				  </div>

				  {/* Lifestyle Recommendations */}
				  <div>
					<h3 className="font-semibold text-lg mb-2">Lifestyle Recommendations:</h3>
					<ul className="list-disc pl-5 space-y-1">
					  {recommendations[getDominantType()].lifestyle.map((item, index) => (
						<li key={index}>{item}</li>
					  ))}
					</ul>
				  </div>
				</div>
				{/* Health Disclaimer */}
				<div className="mt-8 p-4 bg-gray-100 rounded-lg border border-gray-200">
					<h3 className="font-semibold text-lg mb-2">Health Disclaimer</h3>
					<p className="text-sm text-gray-700">
						<strong>Disclaimer:</strong> The contents of this Website are based exclusively upon the opinions of Dr. Cass Ingram, unless otherwise noted. Individual articles are based upon the opinions of Dr. Cass Ingram, who retains copyright, as marked. The information on this Website is not intended to replace an in-person or otherwise relationship with a qualified health care professional and is not intended as medical advice. Rather, it is intended as a means of sharing and exchanging knowledge and information from the research and experience of Ingram and colleagues. Dr. Ingram encourages you to make your own health-related decisions based upon your research and in partnership with a qualified health care professional.
					</p>
				</div>
				{/* Retake Quiz Button */}
				<Button
				  onClick={resetQuiz}
				  className="w-full bg-brand text-white py-2 px-4 rounded hover:bg-brand-dark transition-all"
				>
				  <RotateCcw className="inline mr-2 h-5 w-5" />
				  Retake Quiz
				</Button>
			  </div>
			)}
		  </CardContent>
		</Card>
	  </div>
	);
}